import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoManager = () => {
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  
  const fileInputRef = useRef(null);
  
  // Formulário de publicação
  const [publishForm, setPublishForm] = useState({
    title: '',
    description: '',
    tags: '',
    privacy: 'public'
  });

  useEffect(() => {
    fetchVideos();
    checkYoutubeConnection();
  }, []);

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/videos/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    }
  };

  const checkYoutubeConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/social/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const accounts = response.data.social_accounts || {};
      setYoutubeConnected(!!accounts.youtube?.account_id);
    } catch (error) {
      console.error('Failed to check YouTube connection:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verificar tipo
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|mkv|webm|m4v)$/i)) {
      setError('Formato não suportado. Use MP4, MOV, AVI, MKV ou WEBM.');
      return;
    }

    // Verificar tamanho (500MB)
    if (file.size > 500 * 1024 * 1024) {
      setError('Vídeo muito grande. Máximo: 500MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setMessage('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setMessage(`✅ Vídeo "${response.data.video.original_name}" enviado com sucesso!`);
        fetchVideos();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao enviar vídeo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este vídeo?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Vídeo excluído com sucesso');
      fetchVideos();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao excluir vídeo');
    }
  };

  const openPublishModal = (video) => {
    setSelectedVideo(video);
    setPublishForm({
      title: video.title || video.original_name.replace(/\.[^/.]+$/, ''),
      description: video.description || 'Vídeo publicado via MonetizeMaster',
      tags: 'marketing, afiliados, renda extra',
      privacy: 'public'
    });
  };

  const publishToYoutube = async () => {
    if (!selectedVideo) return;

    setPublishingId(selectedVideo.id);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/videos/${selectedVideo.id}/publish-youtube`,
        null,
        {
          params: {
            title: publishForm.title,
            description: publishForm.description,
            tags: publishForm.tags,
            privacy: publishForm.privacy
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setMessage(`✅ Vídeo publicado no YouTube! URL: ${response.data.youtube_url}`);
        setSelectedVideo(null);
        fetchVideos();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao publicar no YouTube');
    } finally {
      setPublishingId(null);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (video) => {
    if (video.youtube_status === 'published') {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">✅ No YouTube</span>;
    }
    if (video.youtube_status === 'uploading') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">⏳ Enviando...</span>;
    }
    if (video.youtube_status === 'error') {
      return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">❌ Erro</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">📁 Salvo</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">📹 Gerenciador de Vídeos</h1>
        <p className="text-gray-600">Faça upload de seus vídeos e publique diretamente no YouTube</p>
      </div>

      {/* Mensagens */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {message}
          <button onClick={() => setMessage('')} className="absolute top-2 right-2 text-green-700">&times;</button>
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
          <button onClick={() => setError('')} className="absolute top-2 right-2 text-red-700">&times;</button>
        </div>
      )}

      {/* Status do YouTube */}
      {!youtubeConnected && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <span className="text-2xl mr-3">⚠️</span>
            <div>
              <h3 className="font-semibold text-yellow-800">YouTube não conectado</h3>
              <p className="text-sm text-yellow-700">
                Para publicar vídeos no YouTube, primeiro conecte sua conta em{' '}
                <a href="/social-connect" className="underline font-medium">Conectar Redes Sociais</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Área de Upload */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📤 Upload de Vídeo</h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,.mp4,.mov,.avi,.mkv,.webm,.m4v"
            className="hidden"
            id="video-upload"
          />
          
          {uploading ? (
            <div className="space-y-4">
              <div className="animate-pulse text-4xl">📤</div>
              <p className="text-gray-600">Enviando vídeo... {uploadProgress}%</p>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <label htmlFor="video-upload" className="cursor-pointer">
              <div className="text-4xl mb-4">🎬</div>
              <p className="text-xl font-medium text-gray-700 mb-2">
                Clique para selecionar ou arraste um vídeo
              </p>
              <p className="text-sm text-gray-500">
                Formatos: MP4, MOV, AVI, MKV, WEBM • Máximo: 500MB
              </p>
            </label>
          )}
        </div>
      </div>

      {/* Lista de Vídeos */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">📚 Meus Vídeos ({videos.length})</h2>
        </div>

        {videos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">📭</div>
            <p>Nenhum vídeo enviado ainda.</p>
            <p className="text-sm">Faça upload do seu primeiro vídeo acima!</p>
          </div>
        ) : (
          <div className="divide-y">
            {videos.map((video) => (
              <div key={video.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {/* Preview/Thumbnail */}
                    <div className="w-32 h-20 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                      <video
                        src={`${API}/videos/preview/${video.id}`}
                        className="w-full h-full object-cover"
                        muted
                        onMouseOver={(e) => e.target.play()}
                        onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      />
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">{video.title || video.original_name}</h3>
                      <div className="flex items-center space-x-3 mt-1">
                        {getStatusBadge(video)}
                        <span className="text-sm text-gray-500">{formatSize(video.size)}</span>
                        <span className="text-sm text-gray-500">{video.extension}</span>
                      </div>
                      
                      {video.youtube_url && (
                        <a
                          href={video.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                        >
                          🔗 Ver no YouTube
                        </a>
                      )}
                      
                      {video.youtube_error && (
                        <p className="text-xs text-red-600 mt-1">Erro: {video.youtube_error}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!video.youtube_video_id && youtubeConnected && (
                      <button
                        onClick={() => openPublishModal(video)}
                        disabled={publishingId === video.id}
                        className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg text-sm"
                      >
                        {publishingId === video.id ? '⏳ Enviando...' : '📺 Publicar no YouTube'}
                      </button>
                    )}
                    <button
                      onClick={() => deleteVideo(video.id)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title="Excluir"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Publicação */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">📺 Publicar no YouTube</h3>
              <button onClick={() => setSelectedVideo(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título do Vídeo *</label>
                <input
                  type="text"
                  value={publishForm.title}
                  onChange={(e) => setPublishForm({...publishForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  rows={3}
                  value={publishForm.description}
                  onChange={(e) => setPublishForm({...publishForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  maxLength={5000}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={publishForm.tags}
                  onChange={(e) => setPublishForm({...publishForm, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="marketing, afiliados, renda extra"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Privacidade</label>
                <select
                  value={publishForm.privacy}
                  onChange={(e) => setPublishForm({...publishForm, privacy: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="public">🌍 Público</option>
                  <option value="unlisted">🔗 Não listado</option>
                  <option value="private">🔒 Privado</option>
                </select>
              </div>

              <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800">
                ⚠️ <strong>Importante:</strong> Certifique-se de que seu token do YouTube está válido. 
                Tokens expiram em 1 hora.
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={publishToYoutube}
                  disabled={publishingId || !publishForm.title}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg"
                >
                  {publishingId ? '⏳ Publicando...' : '📺 Publicar no YouTube'}
                </button>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dicas */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">💡 Dicas para Vídeos de Marketing</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Duração ideal:</strong> 1-3 minutos para redes sociais, 5-10 minutos para YouTube</li>
          <li>• <strong>Título:</strong> Use palavras-chave relevantes e chamativas</li>
          <li>• <strong>Descrição:</strong> Inclua seu link de afiliado e call-to-action</li>
          <li>• <strong>Tags:</strong> Adicione 5-10 tags relevantes para melhor alcance</li>
          <li>• <strong>Thumbnail:</strong> O YouTube usará um frame do vídeo (edite depois no YouTube Studio)</li>
        </ul>
      </div>
    </div>
  );
};

export default VideoManager;
